// src/pages/chatRoom.js
// UPDATED: Enhanced with unread message clearing logic

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "../hooks/useAuth";
import { 
  useRoomMessages, 
  useRoomUsers, 
  useSendMessage,
  useClearUnreadMessages 
} from "../hooks/useChat";
import { useQueryClient } from '@tanstack/react-query';
import { chatKeys } from "../hooks/useChat";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import ChatHeader from "../components/chat/ChatHeader";
import MessagesList from "../components/chat/MessagesList";
import MessageInput from "../components/chat/MessageInput";

const ChatRoom = () => {
  console.log("🏠 ChatRoom component is rendering! [UPDATED VERSION]");
  
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);
  const queryClient = useQueryClient();

  // Determine chat type based on URL path
  const isTeamChat = window.location.pathname.includes('/chat/team/');
  const chatType = isTeamChat ? 'team' : 'private';

  // React Query hooks for messaging (private or team)
  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useRoomMessages(id, 20, isTeamChat);
  
  const {
    data: roomUsersData = { users: [], roomInfo: {} },
    isLoading: usersLoading,
    error: usersError,
  } = useRoomUsers(id); // Room users API might work for both
  
  const sendMessageMutation = useSendMessage(isTeamChat);
  const clearUnreadMutation = useClearUnreadMessages();

  // Extract data from the response
  const messages = useMemo(() => messagesData?.pages?.flatMap(page => page.messages) || [], [messagesData?.pages]);
  const roomInfo = useMemo(() => messagesData?.pages?.[0]?.roomInfo || roomUsersData.roomInfo || {}, [messagesData?.pages, roomUsersData.roomInfo]);
  const teamInfo = useMemo(() => messagesData?.pages?.[0]?.team || {}, [messagesData?.pages]);
  
  const roomUsers = useMemo(() => {
    if (isTeamChat) {
      // For team chats, we don't have individual user data in the same way
      // Return empty array or team member info if available
      return [];
    }
    
    // For private chats, prefer room data from messages API which includes otherUser
    const messagesRoomData = messagesData?.pages?.[0]?.room;
    if (messagesRoomData?.otherUser) {
      // Create roomUsers array from current user and otherUser
      const roomUsersFromMessages = [messagesRoomData.otherUser];
      
      // Add current user if available
      if (currentUser) {
        roomUsersFromMessages.push({
          ...currentUser,
          isCurrentUser: true
        });
      }
      
      return roomUsersFromMessages;
    }
    
    // Fallback to room users API
    return roomUsersData.users || [];
  }, [messagesData?.pages, roomUsersData.users, currentUser, isTeamChat]);

  const isLoading = messagesLoading || usersLoading;
  const error = messagesError || usersError;

  // Debug logging for component state
  useEffect(() => {
    console.log("=== ChatRoom Component Debug ===");
    console.log("URL id parameter:", id);
    console.log("Current user data:", currentUser);
    console.log("Current user ID (_id):", currentUser?._id);
    console.log("Current user ID (id):", currentUser?.id);
    console.log("Resolved user ID:", currentUser?._id || currentUser?.id);
    console.log("Room users data:", roomUsersData);
    console.log("Room users:", roomUsers);
    console.log("Room users loading:", usersLoading);
    console.log("Room users error:", usersError);
    console.log("================================");
  }, [id, currentUser, roomUsersData, roomUsers, usersLoading, usersError]);

  // Real-time message handling
  useEffect(() => {
    const handleNewMessage = (event) => {
      const { message, roomId } = event.detail;
      
      // Only handle messages for this room
      if (roomId === id) {
        console.log("Real-time message received for current room:", message);
        
        // Invalidate and refetch messages to get the new message
        queryClient.invalidateQueries({ 
          queryKey: chatKeys.messages(id) 
        });
        
        // Also invalidate rooms to update last message and unread count
        queryClient.invalidateQueries({ 
          queryKey: chatKeys.rooms() 
        });
        
        // Auto-scroll to bottom for new messages
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    };

    // Listen for real-time message events
    window.addEventListener('newMessage', handleNewMessage);
    
    return () => {
      window.removeEventListener('newMessage', handleNewMessage);
    };
  }, [id, queryClient]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Clear unread messages when entering the room
  const hasAttemptedClear = useRef(false);
  const roomIdRef = useRef(null);
  
  // Reset attempt flag when room changes
  useEffect(() => {
    if (id !== roomIdRef.current) {
      hasAttemptedClear.current = false;
      roomIdRef.current = id;
    }
  }, [id]);
  
  // Clear unread messages when entering the room (optional - won't block chat functionality)
  useEffect(() => {
    const currentUserId = currentUser?._id || currentUser?.id;
    
    // Only attempt to clear unread messages if we haven't tried yet for this room
    if (id && currentUserId && !hasAttemptedClear.current) {
      console.log("🧹 Attempting to clear unread messages for roomId:", id);
      hasAttemptedClear.current = true; // Mark as attempted to prevent loops
      
      // Try to clear unread messages, but don't let it affect the chat functionality
      clearUnreadMutation.mutate(id, {
        onSuccess: (data) => {
          console.log("✅ Successfully cleared unread messages:", data);
          
          // Update the chat list to reflect cleared unread count
          queryClient.invalidateQueries({ 
            queryKey: chatKeys.rooms() 
          });
        },
        onError: (error) => {
          console.error("❌ Failed to clear unread messages (this won't affect chat functionality):", error);
          console.log("🚫 Chat will continue to work normally despite this error");
          
          // The chat should still work fine even if clearing unread messages fails
          // This is a nice-to-have feature, not essential for chat functionality
        }
      });
    }
  }, [id, currentUser?._id, currentUser?.id, clearUnreadMutation, queryClient]);

  // Handle sending a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendMessageMutation.isPending) return;

    try {
      await sendMessageMutation.mutateAsync({
        roomId: id,
        content: newMessage.trim(),
      });
      setNewMessage("");
      
      // Auto-scroll to bottom after sending
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Handle load more messages
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Get chat title from room users
  const getChatTitle = () => {
    if (isTeamChat) {
      // For team chats, try to get team name from team data
      if (teamInfo?.name) {
        return `${teamInfo.name} Team Chat`;
      }
      const messagesRoomData = messagesData?.pages?.[0]?.room;
      if (messagesRoomData?.name) {
        return messagesRoomData.name;
      }
      if (roomInfo?.name) {
        return roomInfo.name;
      }
      return "Team Chat";
    }
    
    // For private chats, get other user's name
    const messagesRoomData = messagesData?.pages?.[0]?.room;
    if (messagesRoomData?.otherUser) {
      return messagesRoomData.otherUser.fullName || "Unknown User";
    }
    
    // Fallback to room users
    if (roomUsers && roomUsers.length > 0) {
      const currentUserId = currentUser?._id || currentUser?.id;
      const otherUser = roomUsers.find(user => {
        const userId = user._id || user.id;
        return userId && userId !== currentUserId;
      });
      
      if (otherUser) {
        // Use fullName if available, otherwise fall back to firstName + lastName
        if (otherUser.fullName) {
          return otherUser.fullName;
        } else {
          const firstName = otherUser.firstName || "Unknown";
          const lastName = otherUser.lastName || "User";
          return `${firstName} ${lastName}`;
        }
      }
    }
    return t("chat.loading");
  };

  if (isLoading) {
    return <LoadingSpinner message={t("chat.loadingMessages")} />;
  }

  if (error) {
    return <ErrorMessage message={t("errors.failedToLoad")} />;
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Chat Header */}
      <ChatHeader
        title={getChatTitle()}
        roomUsers={roomUsers}
        roomInfo={roomInfo}
        type={chatType}
        onBack={() => navigate("/chat")}
        isOnline={true}
      />

      {/* Messages Container */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Load More Button (at top) */}
          {hasNextPage && (
            <div className="text-center">
              <button
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {isFetchingNextPage ? t("common.loading") : t("chat.loadMore")}
              </button>
            </div>
          )}

          {/* Messages List */}
          <MessagesList
            messages={messages}
            currentUser={currentUser}
            type={chatType}
          />

          {/* Auto-scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="border-t border-gray-200 bg-white p-4">
          <MessageInput
            value={newMessage}
            onChange={setNewMessage}
            onSubmit={handleSendMessage}
            placeholder={t("chat.typeMessage")}
            isLoading={sendMessageMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatRoom;
